import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VersionHistoryCopyAppBar from '../../../../common/VersionHistoryCopyAppBar';
import DocumentHeader from '../../../Documents/components/DocumentHeader';
import RenderRevisionNodes from '../../../Documents/components/RenderRevisionNodes';
import { getProcedureRevisionById } from '../../../../redux/procedureRevisionSlice';
import { setPastePosition } from '../../../../redux/previewSlice';

/**
 * PastePositionSelector Component
 * Allows user to select where to paste the copied node
 * Shows DocumentHeader checkbox and section checkboxes only
 */
const PastePositionSelector = ({ onPositionSelected, onCancel }) => {
  const dispatch = useDispatch();
  const { sourceNode, sourceDocument, targetDocument, originalData } = useSelector(state => state.preview);
  
  // Selection state: 'documentHeader' | 'section' | 'step' | 'substep' | 'child' | null
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [selectedGlobalStepNumber, setSelectedGlobalStepNumber] = useState(null);
  const [selectedSubstepId, setSelectedSubstepId] = useState(null);
  const [selectedSubstepIndex, setSelectedSubstepIndex] = useState(null);
  const [selectedSubstepLetter, setSelectedSubstepLetter] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [selectedChildIndex, setSelectedChildIndex] = useState(null);
  const [selectedChildType, setSelectedChildType] = useState(null);
  const [selectedChildNodeNumber, setSelectedChildNodeNumber] = useState(null);
  
  // Track DocumentHeader checkbox state
  const isDocumentHeaderChecked = selectedType === 'documentHeader';
  const [currentRevision, setCurrentRevision] = useState(null);
  const [revisionStatus, setRevisionStatus] = useState('Not Submitted');
  const [loading, setLoading] = useState(false);

  // Fetch current revision for DocumentHeader
  React.useEffect(() => {
    const fetchCurrentRevision = async () => {
      if (targetDocument && targetDocument.revisionId) {
        try {
          setLoading(true);
          const result = await dispatch(getProcedureRevisionById(targetDocument.revisionId)).unwrap();
          const revisionData = result?.results || result;
          if (revisionData) {
            setCurrentRevision(revisionData);
            // Extract status from status_changes
            const latestStatus = revisionData?.status_changes?.find(sc => sc.is_latest)?.status_changed_to;
            const currentStatus = latestStatus || 'Not Submitted';
            setRevisionStatus(currentStatus);
          }
        } catch (error) {
          console.error('Error fetching current revision:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCurrentRevision();
  }, [targetDocument, dispatch]);

  // Handle DocumentHeader checkbox click
  const handleDocumentHeaderCheckbox = () => {
    // Toggle selection - if already selected, deselect; otherwise select
    if (selectedType === 'documentHeader') {
      setSelectedType(null);
    } else {
      setSelectedType('documentHeader');
      setSelectedSectionId(null);
      setSelectedSectionIndex(null);
      // Clear child selection
      setSelectedChildId(null);
      setSelectedChildIndex(null);
      setSelectedChildType(null);
      setSelectedChildNodeNumber(null);
    }
  };

  // Handle section checkbox click (from RenderRevisionNodes)
  const handleSectionCheckbox = (sectionId, sectionIndex) => {
    // Allow section selection for both section copy and step copy
    // Toggle selection - if this section is already selected, deselect; otherwise select it
    if (selectedType === 'section' && selectedSectionId === sectionId) {
      setSelectedType(null);
      setSelectedSectionId(null);
      setSelectedSectionIndex(null);
    } else {
      setSelectedType('section');
      setSelectedSectionId(sectionId);
      setSelectedSectionIndex(sectionIndex);
      // Clear step selection when section is selected (for step copy)
      if (sourceNode?.nodeType === 'step') {
        setSelectedStepId(null);
        setSelectedStepIndex(null);
        setSelectedGlobalStepNumber(null);
      }
      if (sourceNode?.nodeType === 'substep') {
        setSelectedStepId(null);
        setSelectedStepIndex(null);
        setSelectedGlobalStepNumber(null);
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
      }
      // Clear child selection
      setSelectedChildId(null);
      setSelectedChildIndex(null);
      setSelectedChildType(null);
      setSelectedChildNodeNumber(null);
    }
  };

  // Handle step checkbox click (from RenderRevisionNodes)
  const handleStepCheckbox = (stepId, stepIndex, globalStepNumber, sectionId) => {
    // Allow when copying step or substep or any child node
    const isChildCopy = ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType);
    if (sourceNode?.nodeType === 'step' || sourceNode?.nodeType === 'substep' || isChildCopy) {
      // Toggle selection - if this step is already selected, deselect; otherwise select it
      if (selectedType === 'step' && selectedStepId === stepId) {
        setSelectedType(null);
        setSelectedStepId(null);
        setSelectedStepIndex(null);
        setSelectedGlobalStepNumber(null);
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
      } else {
        setSelectedType('step');
        setSelectedStepId(stepId);
        setSelectedStepIndex(stepIndex);
        setSelectedGlobalStepNumber(globalStepNumber);
        // Also set section info when step is selected
        const section = originalData?.find(s => s.steps?.some(st => st.tree_node_id === stepId));
        if (section) {
          const sectionIndex = originalData.findIndex(s => s.tree_node_id === section.tree_node_id);
          setSelectedSectionId(section.tree_node_id);
          setSelectedSectionIndex(sectionIndex);
        }
        // Clear any previously selected substep
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
        // Clear child selection
        setSelectedChildId(null);
        setSelectedChildIndex(null);
        setSelectedChildType(null);
        setSelectedChildNodeNumber(null);
      }
    }
  };

  // Handle substep checkbox click (from RenderRevisionNodes)
  const handleSubstepCheckbox = (substepId, substepIndex, substepLetter, globalStepNumber, stepId, sectionId) => {
    const isChildCopy = ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType);
    if (sourceNode?.nodeType === 'substep' || isChildCopy) {
      if (selectedType === 'substep' && selectedSubstepId === substepId) {
        setSelectedType(null);
        setSelectedSubstepId(null);
        setSelectedSubstepIndex(null);
        setSelectedSubstepLetter(null);
      } else {
        setSelectedType('substep');
        setSelectedSubstepId(substepId);
        setSelectedSubstepIndex(substepIndex);
        setSelectedSubstepLetter(substepLetter);
        setSelectedStepId(stepId);
        setSelectedGlobalStepNumber(globalStepNumber);
        const section = originalData?.find(s => s.tree_node_id === sectionId);
        if (section) {
          const sectionIndex = originalData.findIndex(s => s.tree_node_id === section.tree_node_id);
          setSelectedSectionId(section.tree_node_id);
          setSelectedSectionIndex(sectionIndex);
        }
        // Clear child selection when substep changes
        setSelectedChildId(null);
        setSelectedChildIndex(null);
        setSelectedChildType(null);
        setSelectedChildNodeNumber(null);
      }
    }
  };

  // Handle child checkbox click
  const handleChildCheckbox = (childId, childType, childIndex, globalStepNumber, stepId, sectionId, substepId, nodeNumber = null) => {
    const isChildCopy = ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType);
    if (!isChildCopy) return;
    if (selectedType === 'child' && selectedChildId === childId) {
      setSelectedType(null);
      setSelectedChildId(null);
      setSelectedChildIndex(null);
      setSelectedChildType(null);
      setSelectedChildNodeNumber(null);
    } else {
      setSelectedType('child');
      setSelectedChildId(childId);
      setSelectedChildIndex(childIndex);
      setSelectedChildType(childType);
      setSelectedChildNodeNumber(nodeNumber);
      setSelectedStepId(stepId);
      setSelectedGlobalStepNumber(globalStepNumber);
      const section = originalData?.find(s => s.tree_node_id === sectionId);
      if (section) {
        const sectionIndex = originalData.findIndex(s => s.tree_node_id === section.tree_node_id);
        setSelectedSectionId(section.tree_node_id);
        setSelectedSectionIndex(sectionIndex);
      }
      setSelectedSubstepId(substepId || null);
    }
  };

  // Handle Paste Into button (when DocumentHeader or section is selected)
  const handlePasteInto = () => {
    if (selectedType === 'documentHeader') {
      // Paste into document (for section copy)
      dispatch(setPastePosition({
        position: 'into',
        targetType: 'document',
        targetId: null,
        targetIndex: null
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'document', null, null);
      }
    } else if (selectedType === 'section' && (sourceNode?.nodeType === 'step' || sourceNode?.nodeType === 'substep')) {
      // Paste into section (for step/substep copy)
      dispatch(setPastePosition({
        position: 'into',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && sourceNode?.nodeType === 'substep') {
      // Paste substep into step
      dispatch(setPastePosition({
        position: 'into',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      // Paste child into step
      dispatch(setPastePosition({
        position: 'into',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep' && ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      // Paste child into substep
      dispatch(setPastePosition({
        position: 'into',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId
      }));
      if (onPositionSelected) {
        onPositionSelected('into', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    }
  };

  // Handle Paste Above button (when section or step is selected)
  const handlePasteAbove = () => {
    if (selectedType === 'section' && sourceNode?.nodeType === 'section') {
      dispatch(setPastePosition({
        position: 'above',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step' && sourceNode?.nodeType === 'step') {
      dispatch(setPastePosition({
        position: 'above',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep' && sourceNode?.nodeType === 'substep') {
      dispatch(setPastePosition({
        position: 'above',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId
      }));
      if (onPositionSelected) {
        onPositionSelected('above', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    }
  };

  // Handle Paste Below button (when section or step is selected)
  const handlePasteBelow = () => {
    if (selectedType === 'section') {
      dispatch(setPastePosition({
        position: 'below',
        targetType: 'section',
        targetId: selectedSectionId,
        targetIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'section', selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'step') {
      dispatch(setPastePosition({
        position: 'below',
        targetType: 'step',
        targetId: selectedStepId,
        targetIndex: selectedStepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'step', selectedStepId, selectedStepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    } else if (selectedType === 'substep') {
      dispatch(setPastePosition({
        position: 'below',
        targetType: 'substep',
        targetId: selectedSubstepId,
        targetIndex: selectedSubstepIndex,
        globalStepNumber: selectedGlobalStepNumber,
        sectionId: selectedSectionId,
        sectionIndex: selectedSectionIndex,
        stepId: selectedStepId
      }));
      if (onPositionSelected) {
        onPositionSelected('below', 'substep', selectedSubstepId, selectedSubstepIndex, selectedGlobalStepNumber, selectedSectionId, selectedSectionIndex);
      }
    }
  };

  // Determine which buttons to show
  const getButtons = () => {
    if (sourceNode?.nodeType === 'section') {
      // For section copy: show buttons for section/document selection
      if (selectedType === 'documentHeader') {
        return [
          { label: 'Paste Into', onClick: handlePasteInto }
        ];
      } else if (selectedType === 'section') {
        return [
          { label: 'Paste Above', onClick: handlePasteAbove },
          { label: 'Paste Below', onClick: handlePasteBelow }
        ];
      }
    } else if (sourceNode?.nodeType === 'step') {
      // For step copy: show buttons for section/step selection
      if (selectedType === 'section') {
        return [
          { label: 'Paste Into', onClick: handlePasteInto }
        ];
      } else if (selectedType === 'step') {
        return [
          { label: 'Paste Above', onClick: handlePasteAbove },
          { label: 'Paste Below', onClick: handlePasteBelow }
        ];
      }
    } else if (sourceNode?.nodeType === 'substep') {
      if (selectedType === 'step') {
        return [
          { label: 'Paste Into', onClick: handlePasteInto }
        ];
      } else if (selectedType === 'substep') {
        return [
          { label: 'Paste Above', onClick: handlePasteAbove },
          { label: 'Paste Below', onClick: handlePasteBelow }
        ];
      }
    }
    // Child copy: enable Paste Into on step/substep, Above/Below on child (button rendering for child click handled elsewhere if needed)
    else if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      if (selectedType === 'step') {
        return [ { label: 'Paste Into', onClick: handlePasteInto } ];
      } else if (selectedType === 'substep') {
        return [ { label: 'Paste Into', onClick: handlePasteInto } ];
      } else if (selectedType === 'child') {
        return [
          { label: 'Paste Above', onClick: () => {
            dispatch(setPastePosition({ position: 'above', targetType: 'child', targetId: selectedChildId, targetIndex: selectedChildIndex, globalStepNumber: selectedGlobalStepNumber, sectionId: selectedSectionId, sectionIndex: selectedSectionIndex, stepId: selectedStepId, substepId: selectedSubstepId }));
            onPositionSelected && onPositionSelected('above','child',selectedChildId,selectedChildIndex,selectedGlobalStepNumber,selectedSectionId,selectedSectionIndex);
          } },
          { label: 'Paste Below', onClick: () => {
            dispatch(setPastePosition({ position: 'below', targetType: 'child', targetId: selectedChildId, targetIndex: selectedChildIndex, globalStepNumber: selectedGlobalStepNumber, sectionId: selectedSectionId, sectionIndex: selectedSectionIndex, stepId: selectedStepId, substepId: selectedSubstepId }));
            onPositionSelected && onPositionSelected('below','child',selectedChildId,selectedChildIndex,selectedGlobalStepNumber,selectedSectionId,selectedSectionIndex);
          } }
        ];
      }
    }
    return [];
  };

  // Custom checkbox handler for sections, steps, and substeps
  const handleNodeCheckbox = (nodeType, nodeData, context) => {
    if (nodeType === 'section') {
      if (sourceNode?.nodeType !== 'substep') {
        handleSectionCheckbox(nodeData.tree_node_id, context.sectionIndex);
      }
    } else if (nodeType === 'step' && (sourceNode?.nodeType === 'step' || ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType) || sourceNode?.nodeType === 'substep')) {
      handleStepCheckbox(
        nodeData.tree_node_id,
        context.stepIndex || 0,
        context.globalStepNumber,
        context.sectionId
      );
    } else if (nodeType === 'substep' && (sourceNode?.nodeType === 'substep' || ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType))) {
      handleSubstepCheckbox(
        nodeData.tree_node_id,
        context.substepIndex,
        context.substepLetter,
        context.globalStepNumber,
        context.stepId,
        context.sectionId
      );
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(nodeType) && ['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      // For canvas nodes, use detail.canvas_tree_node_id if available, otherwise tree_node_id
      const childId = (nodeType === 'canva' || nodeType === 'canvas') 
        ? (nodeData.detail?.canvas_tree_node_id || nodeData.tree_node_id)
        : nodeData.tree_node_id;
      // Normalize canvas type to "canva" for consistency
      const normalizedType = (nodeType === 'canvas') ? 'canva' : nodeType;
      handleChildCheckbox(
        childId,
        normalizedType,
        context.nodeIndex || context.nodeNumber,
        context.globalStepNumber,
        context.stepId,
        context.sectionId,
        context.substepId,
        context.nodeNumber
      );
    }
  };

  // Create custom checkbox state for sections/steps/substeps to show which is selected
  const getCheckboxState = () => {
    if (sourceNode?.nodeType === 'section') {
      // For section copy: only sections selectable
      if (selectedType === 'section' && selectedSectionId) {
        return {
          currentNodeId: selectedSectionId,
          currentNodeType: 'section',
          allChildrenChecked: false,
          allChildrenDisabled: true, // Disable children (steps, substeps) but section itself is enabled
        };
      }
      // No section selected: sections are enabled, but their children are disabled
      return {
        allChildrenDisabled: true, // This will disable steps/substeps, but sections use !isCurrent check
        allChildrenChecked: false,
      };
    } else if (sourceNode?.nodeType === 'step' || (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType) && selectedType === 'step')) {
      // For step copy: sections and steps are selectable
      if (selectedType === 'section' && selectedSectionId) {
        return {
          currentNodeId: selectedSectionId,
          currentNodeType: 'section',
          allChildrenChecked: false,
          allChildrenDisabled: false, // Steps are enabled when copying a step
        };
      } else if (selectedType === 'step' && selectedStepId) {
        return {
          currentNodeId: selectedStepId,
          currentNodeType: 'step',
          allChildrenChecked: false,
          allChildrenDisabled: false,
        };
      }
      // Nothing selected: sections and steps enabled, but their children are disabled
      // For step copy, we need steps to be enabled, so we set allChildrenDisabled to false
      // The StepNode component will handle enabling steps properly
      return {
        allChildrenDisabled: false, // Enable steps when copying a step (nothing selected yet)
        allChildrenChecked: false,
      };
    } else if (sourceNode?.nodeType === 'substep' || (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType) && selectedType === 'substep')) {
      // For substep copy: steps and substeps selectable (child nodes should not have checkboxes)
      // Note: hideChildCheckboxes prop handles hiding child node checkboxes, so allChildrenDisabled can be false
      if (selectedType === 'step' && selectedStepId) {
        return {
          currentNodeId: selectedStepId,
          currentNodeType: 'step',
          allChildrenChecked: false,
          allChildrenDisabled: false, // Steps and substeps should be enabled
        };
      } else if (selectedType === 'substep' && selectedSubstepId) {
        return {
          currentNodeId: selectedSubstepId,
          currentNodeType: 'substep',
          allChildrenChecked: false,
          allChildrenDisabled: false, // Steps and substeps should be enabled
        };
      }
      return {
        allChildrenDisabled: false, // Steps and substeps should be enabled
        allChildrenChecked: false,
      };
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      // Child copy: when a child is selected, mark it as current
      if (selectedType === 'child' && selectedChildId) {
        // Normalize canvas type to "canva" for consistency with CanvasNode expectations
        const normalizedChildType = (selectedChildType === 'canvas') ? 'canva' : (selectedChildType || 'child');
        return {
          currentNodeId: selectedChildId,
          currentNodeType: normalizedChildType,
          allChildrenChecked: false,
          allChildrenDisabled: false,
        };
      }
      return {
        allChildrenDisabled: false,
        allChildrenChecked: false,
      };
    }
    // Child copy: enable steps, substeps and child checkboxes
    else if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      return {
        allChildrenDisabled: false,
        allChildrenChecked: false,
      };
    }
    return {
      allChildrenDisabled: true,
      allChildrenChecked: false,
    };
  };

  if (!targetDocument || !sourceNode || !sourceDocument) {
    return <div>Missing target document or source node data.</div>;
  }

  // Build title based on selection state
  const getTitle = () => {
    const getSourceNodeTypeText = () => {
      if (sourceNode?.nodeType === 'section') return 'Section';
      if (sourceNode?.nodeType === 'step') return 'Step';
      if (sourceNode?.nodeType === 'substep') return 'Substep';
      return sourceNode?.nodeTitle?.split(' ')[0] || '';
    };
    
    const sourceTypeText = getSourceNodeTypeText();
    const sourceNodeTitleWithoutType = sourceTypeText ? sourceNode.nodeTitle?.replace(/^[A-Za-z ]+\s*/, '') || '' : sourceNode.nodeTitle;
    const middlePart = ` from ${sourceDocument.partNumber} Rev ${sourceDocument.revision} to ${targetDocument.partNumber} Rev ${targetDocument.revision}`;
    
    if (sourceNode?.nodeType === 'section') {
      if (selectedType === 'section' && selectedSectionIndex !== null) {
        // Section is selected - append section number with underline
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>Section {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Section {selectedSectionIndex + 1}</span>
          </span>
        );
      }
    } else if (sourceNode?.nodeType === 'step') {
      if (selectedType === 'section' && selectedSectionIndex !== null) {
        // Section is selected - append section number with underline
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>Step {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Section {selectedSectionIndex + 1}</span>
          </span>
        );
      } else if (selectedType === 'step' && selectedGlobalStepNumber !== null) {
        // Step is selected - append step number with underline
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>Step {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Step {selectedGlobalStepNumber}</span>
          </span>
        );
      }
    } else if (sourceNode?.nodeType === 'substep') {
      if (selectedType === 'step' && selectedGlobalStepNumber !== null) {
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>Substep {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Step {selectedGlobalStepNumber}</span>
          </span>
        );
      } else if (selectedType === 'substep' && selectedGlobalStepNumber !== null && selectedSubstepLetter) {
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>Substep {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Substep {selectedGlobalStepNumber}{selectedSubstepLetter}</span>
          </span>
        );
      }
    } else if (['attachment','video','rich-text','form','canva','canvas'].includes(sourceNode?.nodeType)) {
      // Handle child node copying
      if (selectedType === 'step' && selectedGlobalStepNumber !== null) {
        // Step is selected for Paste Into
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>{sourceTypeText} {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Step {selectedGlobalStepNumber}</span>
          </span>
        );
      } else if (selectedType === 'substep' && selectedGlobalStepNumber !== null && selectedSubstepLetter) {
        // Substep is selected for Paste Into
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>{sourceTypeText} {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>Substep {selectedGlobalStepNumber}{selectedSubstepLetter}</span>
          </span>
        );
      } else if (selectedType === 'child' && selectedGlobalStepNumber !== null) {
        // Another child is selected for Paste Above/Below
        // Use nodeNumber if available, otherwise try to find the child node data
        let selectedChildDisplay = '';
        
        if (selectedChildNodeNumber !== null && selectedChildType) {
          // Use nodeNumber for display (e.g., "Rich Text 1", "Attachment 2")
          const childTypeName = selectedChildType === 'rich-text' ? 'Rich Text' :
                               selectedChildType === 'canva' || selectedChildType === 'canvas' ? 'Canvas' :
                               selectedChildType.charAt(0).toUpperCase() + selectedChildType.slice(1);
          selectedChildDisplay = `${childTypeName} ${selectedChildNodeNumber}`;
        } else if (originalData && selectedSectionId && selectedStepId) {
          // Fallback: try to find the selected child node data
          const section = originalData.find(s => s.tree_node_id === selectedSectionId);
          if (section && section.steps) {
            const step = section.steps.find(st => st.tree_node_id === selectedStepId);
            if (step) {
              // Search in step children
              const allStepChildren = [
                ...(step.children || []),
                ...(step.rich_texts || []),
                ...(step.attachments || []),
                ...(step.videos || []),
                ...(step.canvases || []),
                ...(step.forms || [])
              ];
              
              let foundChild = null;
              if (selectedSubstepId) {
                // Search in substep children
                const substep = step.substeps?.find(sub => sub.tree_node_id === selectedSubstepId);
                if (substep) {
                  const allSubstepChildren = [
                    ...(substep.children || []),
                    ...(substep.rich_texts || []),
                    ...(substep.attachments || []),
                    ...(substep.videos || []),
                    ...(substep.canvases || []),
                    ...(substep.forms || [])
                  ];
                  
                  foundChild = allSubstepChildren.find(child => {
                    const childId = (selectedChildType === 'canva' || selectedChildType === 'canvas') 
                      ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                      : child.tree_node_id;
                    return childId === selectedChildId;
                  });
                }
              } else {
                foundChild = allStepChildren.find(child => {
                  const childId = (selectedChildType === 'canva' || selectedChildType === 'canvas') 
                    ? (child.detail?.canvas_tree_node_id || child.tree_node_id)
                    : child.tree_node_id;
                  return childId === selectedChildId;
                });
              }
              
              if (foundChild) {
                // Extract child title/name
                const childTitle = foundChild.title || foundChild.name || foundChild.detail?.name || '';
                const childTitleWithoutType = childTitle.replace(/^[A-Za-z ]+\s*/, '');
                const childTypeName = selectedChildType === 'rich-text' ? 'Rich Text' :
                                     selectedChildType === 'canva' || selectedChildType === 'canvas' ? 'Canvas' :
                                     selectedChildType?.charAt(0).toUpperCase() + selectedChildType?.slice(1) || 'Child';
                selectedChildDisplay = `${childTypeName} ${childTitleWithoutType || (selectedChildIndex !== null ? selectedChildIndex + 1 : '')}`;
              } else {
                const childTypeName = selectedChildType === 'rich-text' ? 'Rich Text' :
                                     selectedChildType === 'canva' || selectedChildType === 'canvas' ? 'Canvas' :
                                     selectedChildType?.charAt(0).toUpperCase() + selectedChildType?.slice(1) || 'Child';
                selectedChildDisplay = childTypeName;
              }
            }
          }
        }
        
        // Final fallback if we couldn't determine the display
        if (!selectedChildDisplay) {
          const childTypeName = selectedChildType === 'rich-text' ? 'Rich Text' :
                               selectedChildType === 'canva' || selectedChildType === 'canvas' ? 'Canvas' :
                               selectedChildType?.charAt(0).toUpperCase() + selectedChildType?.slice(1) || 'Child';
          selectedChildDisplay = childTypeName;
        }
        
        // Just show the child display without step/substep prefix
        return (
          <span>
            Copying <span style={{ textDecoration: 'underline' }}>{sourceTypeText} {sourceNodeTitleWithoutType}</span>{middlePart} <span style={{ textDecoration: 'underline' }}>{selectedChildDisplay}</span>
          </span>
        );
      }
    }
    
    // Nothing selected - still underline the source node type with index
    return (
      <span>
        Copying <span style={{ textDecoration: 'underline' }}>{sourceTypeText} {sourceNodeTitleWithoutType}</span>{middlePart}
      </span>
    );
  };

  const title = getTitle();

  return (
    <div>
      <VersionHistoryCopyAppBar
        title={title}
        subheading=""
        buttons={getButtons()}
        buttonsBeforeTitle={[]}
        showBackButton={false}
        showProfileButton={false}
        showTablesDropdown={false}
        showCancelButton={true}
        onCancel={onCancel}
        showViewToggle={false}
      />

      <div style={{ padding: '20px' }}>
        {/* DocumentHeader - with checkbox for section copy, without checkbox for step copy */}
        <DocumentHeader
          type={targetDocument.type}
          partNumber={targetDocument.partNumber}
          revision={targetDocument.revision}
          revisionId={targetDocument.revisionId}
          partId={targetDocument.partId}
          // description={targetDocument.description}
          description={false}
          status={revisionStatus}
          currentRevision={currentRevision}
          showCheckbox={sourceNode?.nodeType === 'section'}
          showVersionInfo={false}
          isCheckboxEnabled={sourceNode?.nodeType === 'section'}
          isCheckboxChecked={isDocumentHeaderChecked}
          checkboxLabelColor={sourceNode?.nodeType === 'section' ? "rgb(0, 102, 204)" : undefined}
          onCheckboxClick={handleDocumentHeaderCheckbox}
        />

        {/* Render all nodes; when copying a substep, hide section checkboxes so only steps/substeps are selectable */}
        <div style={{ marginTop: '20px' }}>
          <RenderRevisionNodes
            partRef={targetDocument.partId}
            procedureRevRef={targetDocument.revisionId}
            disabled={false}
            showCheckbox={true}
            partNumber={targetDocument.partNumber}
            revision={targetDocument.revision}
            preloadedData={originalData}
            customOnNavigate={handleNodeCheckbox}
            enableOnlySections={sourceNode?.nodeType === 'section'}
            showOnlySectionCheckboxes={sourceNode?.nodeType === 'section'}
            checkboxLabelColor="rgb(0, 102, 204)"
            checkboxState={getCheckboxState()}
            showSectionCheckbox={sourceNode?.nodeType === 'section' || sourceNode?.nodeType === 'step'}
            hideChildCheckboxes={sourceNode?.nodeType === 'substep' || sourceNode?.nodeType === 'step'}
            hideSubstepCheckboxes={sourceNode?.nodeType === 'step'}
          />
        </div>
      </div>
    </div>
  );
};

export default PastePositionSelector;

